import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card, CardContent, Typography, TextField, Button, Stack } from '@mui/material';
import { Page, PageTitle } from '../../../components';
import useFetch from '../../../hooks/useFetch';
import { formatCents } from '../money';

const toBody = (b, matchHint) => ({
  payee: b.payee, amountCents: Number(b.amount_cents), isEstimate: Boolean(b.is_estimate), cadence: b.cadence,
  dayOfMonth: b.day_of_month ?? undefined, intervalWeeks: b.interval_weeks ?? undefined, anchorDate: b.anchor_date ?? undefined,
  dueDate: b.due_date ?? undefined, endDate: b.end_date ?? undefined, bankAccountId: b.bank_account_id, matchHint: matchHint || null,
});

function BillCard({ bill, canManage, fetchResource, onSaved }) {
  const { t } = useTranslation();
  const [hint, setHint] = useState(bill.match_hint || '');
  return (
    <Card sx={{ mb: 1 }}>
      <CardContent>
        <Typography>{bill.payee} · {bill.is_estimate ? '~' : ''}{formatCents(bill.amount_cents)} · {bill.cadence}{bill.day_of_month ? ` ${bill.day_of_month}` : ''}</Typography>
        {bill.last_matched_amount_cents != null && <Typography variant='body2'>{t('calendar.admin.lastMatch')}: {formatCents(bill.last_matched_amount_cents)}</Typography>}
        {canManage && (
          <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
            <TextField size='small' label={t('calendar.admin.hint')} value={hint} onChange={e => setHint(e.target.value)} />
            <Button onClick={async () => { const r = await fetchResource({ resource: `calendar/bills/${bill.id}/edit`, body: toBody(bill, hint) }); if (!r.error) onSaved(); }}>{t('calendar.review.save')}</Button>
            <Button color='warning' onClick={async () => { await fetchResource({ resource: `calendar/bills/${bill.id}/stop` }); onSaved(); }}>{t('calendar.bill.stop')}</Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function CalendarBills({ auth, setAuth }) {
  const { t } = useTranslation();
  const { fetchResource, showAlert, alertMessage } = useFetch({ setAuth });
  const [bills, setBills] = useState([]);
  const load = () => fetchResource({ method: 'GET', resource: 'calendar/bills' }).then(r => setBills((r.bills || []).filter(b => b.active)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Page>
      <PageTitle>{t('calendar.admin.bills')}</PageTitle>
      {showAlert && <Alert severity={alertMessage.type} variant='filled'>{alertMessage.message}</Alert>}
      {bills.map(b => <BillCard key={b.id} bill={b} canManage={Boolean(auth.perms?.CAL_MANAGE)} fetchResource={fetchResource} onSaved={load} />)}
    </Page>
  );
}
